import React, { useState, useEffect, useCallback, useMemo } from "react";
import classNames from "classnames";
import { cn } from "utils";
import { SegmentedProps } from "./types";

/**
 * 分段选择器组件
 * 用于在几个选项中进行单选，常用于时间范围选择等场景
 */
const Segmented: React.FC<SegmentedProps> = ({
  options,
  defaultValue,
  value: propValue,
  onChange,
  containerStyle,
  activeStyle,
  inactiveStyle,
  activeTextStyle,
  inactiveTextStyle,
}) => {
  // 使用useMemo缓存选项值数组，避免不必要的重新计算
  const optionValues = useMemo(
    () => options.map((option) => option.value),
    [options],
  );

  // 默认选择第一个选项（如果未提供默认值）
  const defaultOption = useMemo(() => {
    if (defaultValue && optionValues.includes(defaultValue)) {
      return defaultValue;
    }
    return optionValues[0] || "";
  }, [defaultValue, optionValues]);

  // 内部维护选中状态（非受控模式）
  const [internalValue, setInternalValue] = useState(defaultOption);

  // 判断是否为受控组件
  const isControlled = propValue !== undefined;
  const currentValue = isControlled ? propValue : internalValue;

  // 当options变化且当前值不在选项中时，重置为第一个选项
  useEffect(() => {
    if (!optionValues.includes(currentValue)) {
      const newValue = optionValues[0];
      if (newValue && !isControlled) {
        setInternalValue(newValue);
        onChange?.(newValue);
      }
    }
  }, [optionValues, currentValue, isControlled, onChange]);

  // 处理选项点击
  const handleClick = useCallback(
    (selectedValue: string) => {
      // 如果点击的是当前已选中的选项，不做任何处理
      if (selectedValue === currentValue) {
        return;
      }

      // 查找选项，检查是否禁用
      const option = options.find((opt) => opt.value === selectedValue);
      if (option?.disabled) {
        return;
      }

      if (!isControlled) {
        setInternalValue(selectedValue);
      }
      onChange?.(selectedValue);
    },
    [currentValue, isControlled, onChange, options],
  );

  return (
    <div
      className="flex rounded-md border border-border_white_transparent10 h-[24px] justify-center items-center"
      style={containerStyle}
      role="tablist"
      aria-orientation="horizontal"
    >
      {options.map((option, index) => {
        const isActive = option.value === currentValue;
        const isDisabled = option.disabled === true;
        return (
          <div key={option.value} className="flex flex-row items-center">
            <button
              title={option.label}
              aria-label={option.label}
              data-openreplay-label="misc_segmented_click"
              id="btn-segmented"
              role="tab"
              tabIndex={isActive ? 0 : -1}
              aria-selected={isActive}
              aria-disabled={isDisabled}
              disabled={isDisabled}
              className={classNames(
                "px-3 py-[5px] body_medium_s focus:outline-none rounded-[4px] flex flex-row items-center min-w-[40px] h-[22px]",
                isActive
                  ? "bg-background_white_overlay10 !rounded-[5px] text-content_primary"
                  : " text-content_secondary hover:text-content_secondary/70",
                isDisabled && "opacity-50 cursor-not-allowed",
              )}
              style={isActive ? activeStyle : inactiveStyle}
              onClick={isDisabled ? undefined : () => handleClick(option.value)}
            >
              <span style={isActive ? activeTextStyle : inactiveTextStyle}>
                {option.label}
              </span>
            </button>
            {index < options.length - 1 && (
              <div
                className={cn(
                  "w-[1px] h-[12px] bg-background_white_overlay10",
                  // 当前按钮或下一个按钮选中时隐藏分割线
                  (option.value === currentValue ||
                    currentValue === options[index + 1].value) &&
                    "opacity-0",
                )}
              ></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Segmented;
